'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Hook 用于检测 BlogCategoryTabs 是否吸顶
 * @param selector - BlogCategoryTabs 元素的选择器，默认为 data-blog-category-tabs
 * @returns 是否吸顶的状态
 */
export function useBlogSticky(selector = '[data-blog-category-tabs]') {
  const tabsRef = useRef<HTMLElement | null>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    // 查找 BlogCategoryTabs 元素
    const findTabsElement = () => {
      const tabs = document.querySelector(selector) as HTMLElement;
      if (tabs) {
        tabsRef.current = tabs;
        return true;
      }
      return false;
    };

    if (!findTabsElement()) {
      // 如果还没找到，等待一下再试
      const timer = setTimeout(() => {
        findTabsElement();
      }, 100);
      return () => clearTimeout(timer);
    }

    const checkSticky = () => {
      if (!tabsRef.current) return;

      const rect = tabsRef.current.getBoundingClientRect();
      // BlogCategoryTabs 的 sticky top 是 156px (桌面) 或 118px (移动端)
      // 当元素距离顶部小于等于 sticky top 时，说明已经吸顶
      const stickyTop = window.innerWidth >= 640 ? 156 : 114;
      const isCurrentlySticky = rect.top <= stickyTop;

      setIsSticky(isCurrentlySticky);
    };

    checkSticky();
    window.addEventListener('scroll', checkSticky, { passive: true });
    window.addEventListener('resize', checkSticky);

    return () => {
      window.removeEventListener('scroll', checkSticky);
      window.removeEventListener('resize', checkSticky);
    };
  }, [selector]);

  return isSticky;
}
